import { treeToJSON } from "./Graph";
import { getGameInfo, MAXNUMBEROFSUGGESTIONS, isAdmissibleSuggestion } from "./App.js";
import axios from 'axios';
import { API_BASE_IP } from "./ApiClient.js";

async function fetchWithTimeout(resource, options = {}) {
  const { timeout = 2000 } = options;
  
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  const response = await fetch(resource, {
    ...options,
    signal: controller.signal  
  });
  clearTimeout(id);

  return response;
}

class Server {

    constructor() {
      this.data = {
        analyzedMoves: {},
        analysisComplete: false,
        requestedMoves: new Set(), 
        currentlyFetching: false, 
        waitingOnKatago: false, 
        inGameVariation: false, 
        variationAnalysisComplete: false, 
        gameId: null, 
        variationHash: null, 
        variationId: null, 
        depth: null, 
        onGamePage: true, 
        username: undefined,
      };
  
      this.subscribers = [];
    }
  
    // Subscribe a callback to notify on data updates
    subscribe(callback) {
      this.subscribers.push(callback);
    }
  
    // Unsubscribe a callback
    unsubscribe(callback) {
      this.subscribers = this.subscribers.filter((sub) => sub !== callback);
    }
  
    // Notify all subscribers of a data change
    notifySubscribers() {
      this.subscribers.forEach((callback) => callback(this.data));
    }

    // Read Katago server and update analyzedMoves
    async fetchResultsAndUpdateAnalyzedMoves(intervalFetching, fetchingGameId, gameLength, threadType, isVariation = false, moveId = null) {

      const redisKey = `${fetchingGameId}:${threadType}`
      const currentRedisKey = `${this.data.gameId}:${this.data.depth}`

      console.log(currentRedisKey, redisKey)

      if(!isVariation && this.data.gameId !== null && currentRedisKey !== redisKey ) {
        console.log("gameId:",this.data.gameId)
        console.log("type:",threadType)
        console.log("Closing fetching interval request for game", fetchingGameId)
        clearInterval(intervalFetching) // This is a stale request from a prior upload so quit 
        return 
      } else if (!this.data.onGamePage) { // user navigated away from game page
        console.log("Closing fetching interval request for game", fetchingGameId)
        clearInterval(intervalFetching) // This is a stale request from a prior upload so quit 
        return 
      }

      if(isVariation) {
          this.data.variationAnalysisComplete = false
      }

      console.debug("Fetching analysis data for game",redisKey)
      
      if(!this.data.inGameVariation && this.data.analysisComplete) {

        clearInterval(intervalFetching); 
        
      } else if (!this.data.inGameVariation) { // get analyses on the main line

        try {
          this.data.currentlyFetching = true
          const response = await fetchWithTimeout(`https://${API_BASE_IP}/results/${this.data.username}/${redisKey}`, {
            timeout: 2000
          });
          const results = await response.json();

          const updatedAnalyzedMoves = Object.entries(results).map(([redisKey, jsonMoveData]) => {

            const [moveId, analyzedThreadType] = redisKey.split(":")
            const parsedMoveData = JSON.parse(jsonMoveData);
            const turn = parseInt(parsedMoveData.turnNumber); 

            // We only take the top suggested moves from KataGo
            const topTenMoveData = {
              ...parsedMoveData, 
              moveInfos: parsedMoveData.moveInfos.slice(0,MAXNUMBEROFSUGGESTIONS)
              }

            // We also only take moves with sufficiently large edge weights
            const topMoveData = {
              ...topTenMoveData, 
              moveInfos: topTenMoveData.moveInfos.filter(moveInfo => isAdmissibleSuggestion(moveInfo,topTenMoveData))}
            return {moveId: moveId, turn: turn, data: topMoveData, depth: analyzedThreadType}
          });
          console.log("In fetch, updatedAnalyzedMoves =",updatedAnalyzedMoves)

          this.data.analyzedMoves = {...this.data.analyzedMoves, [fetchingGameId]: updatedAnalyzedMoves}; // Update analyzed data 
          this.data.currentlyFetching = false

          this.notifySubscribers(); // Notify subscribers about the potential update

          // Check to see if we're done analyzing. Game length is the number of moves in the game,
          // while analyzedMoves is the number of moves we've received from the server; 
          // since the empty initial board is also analyzed by the server, but not counted in 
          // the game length, we need to add 1 to the game length to compare the two.
          if (this.data.analyzedMoves[fetchingGameId].length === gameLength + 1) {
              this.data.analysisComplete = true
              this.data.waitingOnKatago = false
              console.debug("Analysis complete because we received",
                this.data.analyzedMoves[fetchingGameId].length, 
                "analyzed moves at depth",threadType,
                "and the game length is",gameLength)
              clearInterval(intervalFetching); 
              this.notifySubscribers(); // Notify subscribers so status flags get updated
          } 

          return updatedAnalyzedMoves
      } catch (error) {
          // Timeouts if the request takes too long 
          console.log("Fetch request for game",redisKey,"timed out.");
          this.data.currentlyFetching = false; 
          return; 
        }
      } else if(this.data.inGameVariation && !this.data.variationAnalysisComplete) { // fetch a variation

        try {
          this.data.currentlyFetching = true
          const response = await fetchWithTimeout(`https://${API_BASE_IP}/results/${this.data.username}/${redisKey + "!"}`, {
            timeout: 2000
          });
          const results = await response.json();

          // console.log("Received results from backend:",results)

          const updatedAnalyzedMoves = Object.entries(results).map(([redisKey, jsonMoveData]) => {

            const [moveId, analyzedThreadType] = redisKey.split(":")
            const parsedMoveData = JSON.parse(jsonMoveData);
            const turn = parseInt(parsedMoveData.turnNumber); 

            // We only take the top suggested moves from KataGo
            const topTenMoveData = {
              ...parsedMoveData, 
              moveInfos: parsedMoveData.moveInfos.slice(0,MAXNUMBEROFSUGGESTIONS)
              }

            // We also only take moves with sufficiently large edge weights
            const topMoveData = {
              ...topTenMoveData, 
              moveInfos: topTenMoveData.moveInfos.filter(moveInfo => isAdmissibleSuggestion(moveInfo,topTenMoveData))}
            return {moveId: moveId, turn: turn, data: topMoveData, depth: analyzedThreadType}
          });
          console.log("In fetch, updatedAnalyzedMoves =",updatedAnalyzedMoves)

          const analyzedMove = updatedAnalyzedMoves.find(x => x.moveId === moveId + "!")
          // console.log(analyzedMove,moveId)
          if(analyzedMove) {
            // console.log("Best moves:",analyzedMove?.data?.moveInfos?.map(x => x.move + " " + x.edgeVisits))
            this.data.analyzedMoves = {...this.data.analyzedMoves, [moveId]: [analyzedMove]}; // Update analyzed data 
            this.data.variationAnalysisComplete = true
            this.notifySubscribers(); // Notify subscribers about the update
            console.debug("Analysis completed.")
            clearInterval(intervalFetching); 

          return updatedAnalyzedMoves
          }
          this.data.currentlyFetching = false

          return; 
        } catch (error) {
          // Timeouts if the request takes too long 
          console.log("Fetch request for variation",moveId,"timed out.");
          this.data.currentlyFetching = false; 
          return; 
        }
      }
    };

    // Post moves to Katago server for analysis
    async postMovesToKatagoForAnalysis (
        tree,
        currents = {},  
        nodeId = null, 
        alreadyAnalyzed = new Set(), 
        threadType
      ) {

      // console.debug("post called with",alreadyAnalyzed)

      const treeInJSON = { 
        ... treeToJSON(tree, currents, nodeId, alreadyAnalyzed),
        threadType: threadType, 
      } 

      // Check to see if this was a batch request (nodeId === null) and 
      // if all the moves were already analyzed.
      if(nodeId === null && 'analyzeTurns' in treeInJSON.katagoRequest && treeInJSON.katagoRequest.analyzeTurns.length === 0) 
        return // then don't post anything

      const onlyAnalyzeLastMove = nodeId !== null
      const nodes = nodeId ? [...tree.listNodesVertically(nodeId,-1,{})].reverse() : [...tree.listCurrentNodes(currents)]
      const numberOfMovesUpToGivenMove = nodes.length - 1

      try {
        if (onlyAnalyzeLastMove) {
          this.data.requestedMoves.add(treeInJSON.moveId);
        } else {
          if(treeInJSON.katagoRequest?.analyzeTurns?.length > 0) {
            this.data.requestedMoves = this.data.requestedMoves.union(new Set(treeInJSON.katagoRequest.analyzeTurns))
          }
        }
          console.debug("Posting moves to Katago to be analyzed:", treeInJSON)
          const response = await axios.post(`https://${API_BASE_IP}/analyze/`, treeInJSON);
          this.data.waitingOnKatago=true;
          console.log("Post request finished.")
          return; 

      } catch (err) {
        console.error('Failed to fetch analysis. Axios error:', err.response || err.message);
        this.data.waitingOnKatago=false;
        return; 
      } finally {
        this.data.waitingOnKatago=false;
        return; 
      }
    }

    initialFetch(tree, currents, threadType) {
            
      const {gameId, gameLength} = getGameInfo(tree,currents)
      this.data.requestedMoves = new Set(); // Reset the requested moves since we're working with a new game tree 
      this.data.analysisComplete = false; 

      let intervalFetching = null; 

      // Fetch the updated analysis data from the server, 
      // and then request moves that haven't yet been analyzed,  
      // passing them so we know which moves not to request again. 
      // TODO Unfortunately postMoves...() is running before fetchResults...() 
      // is finished running, resulting in postMoves...() receiving an undefined object. 
      (async () => {
  
        console.debug("Initial fetching of results to see what's already been computed for game", gameId)
        const updatedAnalyzedMoves = await this.fetchResultsAndUpdateAnalyzedMoves(null, gameId, gameLength, threadType)

        // We only want to request moves which we haven't requested already, so 
        // keep track of the moves to be analyzed that have *already* been requested
        this.data.requestedMoves=this.data.requestedMoves.union(
          new Set( updatedAnalyzedMoves ? updatedAnalyzedMoves.map(x=>x.turn) : [] )
        )

        this.postMovesToKatagoForAnalysis(tree,currents, null, updatedAnalyzedMoves?.map(x => x.turn), threadType)
      })()
  
      // Make period requests every 2 seconds to fetch updated analysis results from the Katago server
      intervalFetching = setInterval((() => this.fetchResultsAndUpdateAnalyzedMoves(intervalFetching, gameId, gameLength, threadType)), 2000);
  
    }

    async analyze(tree, currents = {}, nodeId = null,treeInJSONPrecomputed = null, threadType) {
      try {
        const treeInJSON = treeInJSONPrecomputed ? treeInJSONPrecomputed : treeToJSON(tree, currents, nodeId);
        const {gameId, gameLength} = getGameInfo(tree,currents)
        const moveId = nodeId !== null ? treeInJSON.moveId : null;
        const variationHash = moveId;
        this.data.variationHash = variationHash;
        
        console.debug("Starting analysis for variation:", variationHash);

        // Fetch initial results for the variation since it's not stored locally
        const updatedAnalyzedMoves = await this.fetchResultsAndUpdateAnalyzedMoves(null, gameId, gameLength, threadType, true, variationHash);
    
        // See if variation has already been analyzed and stored on server
        if (updatedAnalyzedMoves && updatedAnalyzedMoves.find((x) => x.moveId === variationHash && x.depth === threadType)) {
          console.debug("Variation already analyzed:", variationHash);
          this.data.variationAnalysisComplete = true;
          this.notifySubscribers(); // Notify subscribers 
          return;
        }
    
        // If not analyzed, start posting the moves for analysis
        this.data.variationAnalysisComplete = false;
        this.postMovesToKatagoForAnalysis(tree, currents, nodeId, undefined, threadType);
    
        // Start polling the server for updated results
        const intervalFetching = setInterval(async () => {
          try {
            console.debug("Polling for variation analysis:", variationHash);
            const results = await this.fetchResultsAndUpdateAnalyzedMoves(intervalFetching, gameId, gameLength, threadType, true, variationHash);
    
            if (results && results.find((x) => x.moveId === variationHash && x.depth === threadType)) {
              console.debug("Completed analysis for variation", variationHash);
              this.data.variationAnalysisComplete = true;
              return; 
            } else {
              console.debug("No results obtained from fetch for variation",variationHash)
              return; 
            }
          } catch (err) {
            console.error("Error during polling for variation analysis:", err.message);
            clearInterval(intervalFetching);
            return; 
          }
        }, 2000);

        return; 
      } catch (error) {
        console.error("Error in analyze():", error.message);
        return; 
      }
    }
  }
  
  const server = new Server();
  export default server;